// 📄 src/pages/AutoReplyBuilder/AutoReplyBuilder.jsx
import React, { useRef, useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Workflow, LayoutList, Loader2, Pencil, Plus, Settings, X } from "lucide-react";
import AutoReplyCanvas from "./components/AutoReplyCanvas";
import AutoReplySidebar from "./components/AutoReplySidebar";
import AutoReplyFlowsModal from "./components/AutoReplyFlowsListModal";
import AutoReplyConfirmModal from "./components/AutoReplyConfirmModal";
import AutoReplyAlertModal from "./components/AutoReplyAlertModal";
import AutoReplyRecoveryOptionsModal from "./components/AutoReplyRecoveryOptionsModal";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { useAuth } from "../../app/providers/AuthProvider";
import {
  normalizeChoiceTerms,
  validateChoiceConfig,
} from "./utils/choiceValidation";
import {
  canonicalizeAutoReplyNodeType,
  validateAutoReplyGraph,
} from "./utils/graphValidation";
import { validateInputCaptureConfig } from "./utils/inputCaptureValidation";
import { validateConditionConfig } from "./utils/conditionValidation";
import { validateDecisionTableConfig } from "./utils/decisionTableValidation";
import { normalizeFollowUpForSave } from "./components/AutoReplyFollowUpEditor";
import {
  normalizeBrowseCatalogueConfig,
  validateBrowseCatalogueConfig,
} from "./utils/browseCatalogueValidation";
import { validateSearchCatalogueConfig } from "./utils/searchCatalogueValidation";
import {
  normalizePresentCatalogueCommerceConfig,
  PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT,
  validatePresentCatalogueResultsConfig,
} from "./utils/presentCatalogueResultsValidation";
import {
  REVIEW_AND_SUBMIT_DEFAULT_PROMPT,
  validateReviewAndSubmitConfig,
} from "./utils/reviewAndSubmitValidation";
import {
  normalizePrepareCommerceServiceConfig,
  validatePrepareCommerceServiceConfig,
} from "./utils/prepareCommerceServiceValidation";
import { validateWhatsAppFlowConfig } from "./utils/whatsAppFlowValidation";
import {
  normalizeUrlCtaConfig,
  validateUrlCtaConfig,
} from "./utils/urlCtaValidation";

const getValidationNodeLabel = node =>
  String(node?.data?.label || node?.id || "Node").trim() || "Node";

const DEFAULT_ENTRY_SENDER_REQUIRED_MESSAGE =
  "Default Entry requires a specific WhatsApp sender. Please select a WhatsApp number or turn off Default Entry.";
const METADATA_ACTIVATION_ERROR_CODES = new Set([
  "AUTOREPLY_SENDER_REQUIRED",
  "AUTOREPLY_SENDER_INVALID",
  "AUTOREPLY_TRIGGER_REQUIRED",
  "AUTOREPLY_DEFAULT_ENTRY_CONFLICT",
  "AUTOREPLY_DUPLICATE_KEYWORDS",
]);

const formatSenderDisplayLabel = number => {
  if (!number) return "";
  const phone = String(
    number.whatsAppBusinessNumber ||
      number.displayPhoneNumber ||
      number.phoneNumber ||
      number.phoneNumberId ||
      ""
  ).trim();
  const name = String(
    number.senderDisplayName || number.displayName || number.name || ""
  ).trim();

  if (phone && name) return `${phone} (${name})`;
  return phone || name || "";
};

const mapRecoverySummaryStatus = settings => {
  if (!settings || settings.id == null) return "not_configured";
  return settings.isEnabled ? "enabled" : "disabled";
};

export default function AutoReplyBuilder() {
  const { business } = useAuth() || {};
  const canvasRef = useRef();
  const isSavingRef = useRef(false);
  const pendingActivationErrorRef = useRef(null);
  const [searchParams] = useSearchParams();
  const flowId = searchParams.get("flowId");
  const [currentFlowId, setCurrentFlowId] = useState(flowId || null);
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);

  // flows modal
  const [showFlowsModal, setShowFlowsModal] = useState(false);

  // 🛡️ confirmation modal for unsaved changes
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFlowId, setPendingFlowId] = useState(null);
  const [duplicateKeywordModal, setDuplicateKeywordModal] = useState({
    open: false,
    duplicatedKeywords: [],
    conflictingFlows: [],
  });

  // 🔧 flow-level advanced settings (optional)
  const [matchMode, setMatchMode] = useState("EXACT"); // EXACT (default), PHRASE (advanced)
  const [priority, setPriority] = useState(0); // higher wins when multiple flows match
  const [isActive, setIsActive] = useState(false);
  const [isDraft, setIsDraft] = useState(true);
  const [isDefaultEntryFlow, setIsDefaultEntryFlow] = useState(false);
  const [flowName, setFlowName] = useState("");
  const [triggerKeywords, setTriggerKeywords] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [savedPhoneNumberId, setSavedPhoneNumberId] = useState(null);
  const [loadedLegacyFlow, setLoadedLegacyFlow] = useState(false);
  const [isCanvasDirty, setIsCanvasDirty] = useState(false);
  const [hasUserNodes, setHasUserNodes] = useState(false);
  const [isSavingFlow, setIsSavingFlow] = useState(false);
  const [savingMode, setSavingMode] = useState(null);
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [showAdvancedAutomation, setShowAdvancedAutomation] = useState(false);
  const [showNewFlowModal, setShowNewFlowModal] = useState(false);
  const [showNewFlowConfirm, setShowNewFlowConfirm] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoverySummaryStatus, setRecoverySummaryStatus] = useState("unknown");
  const [newFlowDraft, setNewFlowDraft] = useState({
    flowName: "",
    triggerKeywords: "",
    phoneNumberId: "",
    isDefaultEntryFlow: false,
  });

  const toGuidOrNull = value =>
    typeof value === "string" && /^[0-9a-fA-F-]{36}$/.test(value)
      ? value
      : null;
  const activeFlowId = currentFlowId || flowId || null;

  const handlePriorityChange = value => {
    const parsed = Number(value);
    setPriority(Number.isFinite(parsed) ? parsed : 0);
  };

  const markMetadataDirty = () => {
    setIsCanvasDirty(true);
  };

  const updateFlowName = value => {
    setFlowName(value);
    markMetadataDirty();
  };

  const updateTriggerKeywords = value => {
    setTriggerKeywords(value);
    markMetadataDirty();
  };

  const updatePhoneNumberId = value => {
    setPhoneNumberId(value);
    if (value) setLoadedLegacyFlow(false);
    markMetadataDirty();
  };

  const updateMatchMode = value => {
    setMatchMode(value || "EXACT");
    markMetadataDirty();
  };

  const updatePriority = value => {
    handlePriorityChange(value);
    markMetadataDirty();
  };

  const updateIsDefaultEntryFlow = value => {
    setIsDefaultEntryFlow(value);
    markMetadataDirty();
  };

  const normalizeMatchMode = useCallback(rawMode => {
    const mode = String(rawMode || "").trim().toUpperCase();
    if (mode === "PHRASE") return "PHRASE";
    if (mode === "WORD" || mode === "CONTAINS" || mode === "STARTSWITH") {
      return "PHRASE";
    }
    return "EXACT";
  }, []);

  const handleAddNodeFromSidebar = nodeType => {
    canvasRef.current?.addNodeFromSidebar?.(nodeType);
  };

  const handleSaveCanvasFlow = mode => {
    if (isSavingRef.current) return;
    if (mode === "draft") {
      canvasRef.current?.saveDraftFlow?.();
    } else if (mode === "activate") {
      canvasRef.current?.saveAndActivateFlow?.();
    } else if (mode === "save_active") {
      canvasRef.current?.saveActiveChanges?.();
    }
  };

  const handleActivateCanvasFlow = () =>
    handleSaveCanvasFlow("activate");

  const handleDeactivateFlow = async () => {
    if (!activeFlowId || isSavingRef.current) return;
    if (isCanvasDirty) {
      toast.error("Save the active flow before deactivating it.");
      return;
    }

    isSavingRef.current = true;
    setIsSavingFlow(true);
    try {
      await axiosClient.patch(`autoreplyflows/${activeFlowId}/status`, {
        isActive: false,
      });
      setIsActive(false);
      setIsDraft(false);
      setSidebarRefreshKey(key => key + 1);
      toast.success("Flow deactivated.");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to deactivate flow."
      );
    } finally {
      isSavingRef.current = false;
      setIsSavingFlow(false);
      setSavingMode(null);
    }
  };

  const handleOpenFlowsModal = () => {
    setShowEditDetails(false);
    setShowAdvancedAutomation(false);
    setShowRecoveryModal(false);
    setShowNewFlowModal(false);
    setShowNewFlowConfirm(false);
    setShowFlowsModal(true);
  };

  const selectedPhoneNumber = phoneNumbers.find(
    number => number.phoneNumberId === phoneNumberId
  );
  const senderLabel = selectedPhoneNumber
    ? formatSenderDisplayLabel(selectedPhoneNumber)
    : phoneNumberId
    ? phoneNumberId
    : !phoneNumberId && (activeFlowId || loadedLegacyFlow)
    ? "All Number"
    : "No sender selected";
  const visibleFlowName = flowName?.trim() || "Untitled flow";
  const senderChangedAfterLoad =
    activeFlowId &&
    savedPhoneNumberId !== null &&
    (phoneNumberId || "") !== (savedPhoneNumberId || "");

  // ✅ SAVE FLOW handler (connected to <AutoReplyCanvas />)
  const handleSaveFlow = async (payload, options = {}) => {
    if (isSavingRef.current) {
      return { ok: false, reason: "SAVE_IN_PROGRESS" };
    }

    const mode =
      options?.mode ||
      (options?.activate === true
        ? "activate"
        : isActive
        ? "save_active"
        : "draft");
    const activating = mode === "activate";
    const savingDraft = mode === "draft";
    const savingActive = mode === "save_active";
    let persistedDraftData = null;
    isSavingRef.current = true;
    setIsSavingFlow(true);
    setSavingMode(mode);

    const stopSaving = () => {
      isSavingRef.current = false;
      setIsSavingFlow(false);
      setSavingMode(null);
    };

    const failWithErrorToast = (message, result, options) => {
      stopSaving();
      toast.error(message, options);
      return persistedDraftData
        ? {
            ...result,
            persistedDraft: true,
            flowId: persistedDraftData?.id || activeFlowId || null,
          }
        : result;
    };

    try {
      const flowName = payload.name?.trim() || "";
      const triggerKeyword = payload.triggerKeyword?.trim() || "";
      const nodes = Array.isArray(payload.nodes) ? payload.nodes : [];
      const edges = Array.isArray(payload.edges) ? payload.edges : [];
      const graphValidation = validateAutoReplyGraph(nodes, edges);
      const requiresExecutableValidation = activating || savingActive;

      if (!flowName) {
        return failWithErrorToast("Flow name is required.", { ok: false, reason: "VALIDATION" });
      }

      const parsedPriority = Number(priority);
      const safePriority = Number.isNaN(parsedPriority) ? 0 : parsedPriority;
      const createStorageDto = () => ({
        id: toGuidOrNull(payload.id) || toGuidOrNull(activeFlowId) || null,
        phoneNumberId: phoneNumberId || null,
        name: flowName,
        triggerKeyword,
        isActive: false,
        isDraft: true,
        isDefaultEntryFlow,
        matchMode: normalizeMatchMode(matchMode),
        priority: safePriority,
        nodes: nodes.map((node, index) => ({
          id: toGuidOrNull(node.id),
          nodeType:
            node.type === "choice"
              ? "button_choice"
              : node.type === "condition"
              ? "branch"
              : node.type,
          label: node.data?.label || "",
          nodeName: node.data?.label || node.id,
          configJson: JSON.stringify(node.data?.config || {}),
          positionX: node.position?.x ?? 0,
          positionY: node.position?.y ?? 0,
          order: index,
        })),
        edges: edges.map(edge => ({
          id: edge.id || null,
          sourceNodeId: edge.sourceNodeId,
          targetNodeId: edge.targetNodeId,
          sourceHandle: edge.sourceHandle ?? null,
          targetHandle: edge.targetHandle ?? null,
        })),
      });

      if (activating && (isCanvasDirty || !activeFlowId)) {
        const draftResponse = await axiosClient.post(
          "autoreplyflows/draft",
          createStorageDto(),
          {
            __suppressQuotaRedirect: true,
            __silentGenericToast: true,
          }
        );
        persistedDraftData = draftResponse?.data || {};
        const persistedId = persistedDraftData?.id || activeFlowId;
        if (!persistedId) {
          return failWithErrorToast("Save the flow before activating it.", {
            ok: false,
            reason: "VALIDATION",
          });
        }
        setCurrentFlowId(persistedId);
        setIsDraft(true);
        setIsActive(false);
        setIsCanvasDirty(false);
      }

      if (requiresExecutableValidation && !triggerKeyword && !isDefaultEntryFlow) {
        return failWithErrorToast("At least 1 trigger keyword is required.", { ok: false, reason: "VALIDATION" });
      }

      if (requiresExecutableValidation && !graphValidation.ok) {
        return failWithErrorToast(graphValidation.message, {
          ok: false,
          reason: "VALIDATION",
          nodeId: graphValidation.nodeId || null,
          message: graphValidation.message,
        });
      }

      const isExistingFlow = Boolean(toGuidOrNull(payload.id)) || Boolean(activeFlowId);
      if (requiresExecutableValidation && isDefaultEntryFlow && !phoneNumberId) {
        return failWithErrorToast(DEFAULT_ENTRY_SENDER_REQUIRED_MESSAGE, { ok: false, reason: "VALIDATION" });
      }

      if (requiresExecutableValidation && !phoneNumberId && !isExistingFlow) {
        return failWithErrorToast("Select the WhatsApp number that should run this flow.", { ok: false, reason: "VALIDATION" });
      }

      if (requiresExecutableValidation) {
        for (const node of nodes) {
        const nodeType = canonicalizeAutoReplyNodeType(node?.type);

        if (nodeType === "button_choice") {
          const validation = validateChoiceConfig(node?.data?.config || {});
          if (!validation.isValid) {
            const validationMessage =
              validation.globalErrors[0] ||
              validation.optionErrors.find(
                entry => entry?.label || entry?.value || entry?.description
              )?.label ||
              validation.optionErrors.find(
                entry => entry?.label || entry?.value || entry?.description
              )?.value ||
              validation.optionErrors.find(
                entry => entry?.label || entry?.value || entry?.description
              )?.description ||
              "Choice node contains invalid WhatsApp interactive content.";
            const nodeLabel = getValidationNodeLabel(node);
            return failWithErrorToast(`${nodeLabel}: ${validationMessage}`, {
              ok: false,
              reason: "VALIDATION",
              nodeId: node.id,
              message: validationMessage,
            });
          }
        }

        if (["form", "form_input", "input"].includes(nodeType)) {
          return failWithErrorToast("Legacy form/input nodes cannot be saved as executable Input Capture nodes.", {
            ok: false,
            reason: "VALIDATION",
            nodeId: node.id,
            message: "Legacy form/input node type is unsupported.",
          });
        }

        if (nodeType === "input_capture") {
          const validation = validateInputCaptureConfig(node?.data?.config || {});
          if (!validation.isValid) {
            return failWithErrorToast(`${getValidationNodeLabel(node)}: ${validation.message}`, {
              ok: false,
              reason: "VALIDATION",
              nodeId: node.id,
              message: validation.message,
            });
          }
        }

        if (nodeType === "branch") {
          if (node?.data?.legacyNodeType === "condition") {
            return failWithErrorToast("Legacy Condition nodes must be replaced before saving.", {
              ok: false, reason: "VALIDATION", nodeId: node.id,
              message: "Legacy condition node type is unsupported.",
            });
          }
          const validation = validateConditionConfig(node?.data?.config || {});
          if (!validation.isValid) {
            return failWithErrorToast(`${getValidationNodeLabel(node)}: ${validation.message}`, {
              ok: false, reason: "VALIDATION", nodeId: node.id, message: validation.message,
            });
          }
        }

        if (nodeType === "decision_table") {
          const validation = validateDecisionTableConfig(node?.data?.config || {});
          if (!validation.isValid) {
            return failWithErrorToast(`${getValidationNodeLabel(node)}: ${validation.message}`, {
              ok: false,
              reason: "VALIDATION",
              nodeId: node.id,
              message: validation.message,
            });
          }
        }

        if (nodeType === "url_cta") {
          const validation = validateUrlCtaConfig(node?.data?.config || {});
          if (!validation.isValid) {
            return failWithErrorToast(
              `${getValidationNodeLabel(node)}: ${validation.message}`,
              {
                ok: false,
                reason: "VALIDATION",
                nodeId: node.id,
                message: validation.message,
              }
            );
          }
        }

        if (nodeType === "browse_catalogue") {
          const validation = validateBrowseCatalogueConfig(
            node?.data?.config || {}
          );
          if (!validation.isValid) {
            return failWithErrorToast(
              `${getValidationNodeLabel(node)}: ${validation.message}`,
              {
                ok: false,
                reason: "VALIDATION",
                nodeId: node.id,
                message: validation.message,
              }
            );
          }
        }

        if (nodeType === "search_catalogue") {
          const validation = validateSearchCatalogueConfig(
            node?.data?.config || {},
            payload.nodes,
            payload.edges,
            node.id
          );
          if (!validation.isValid) {
            return failWithErrorToast(`${getValidationNodeLabel(node)}: ${validation.message}`, {
              ok: false,
              reason: "VALIDATION",
              nodeId: node.id,
              message: validation.message,
            });
          }
        }

        if (nodeType === "present_catalogue_results") {
          const validation = validatePresentCatalogueResultsConfig(
            node?.data?.config || {},
            payload.nodes
          );
          if (!validation.isValid) {
            return failWithErrorToast(
              `${getValidationNodeLabel(node)}: ${validation.message}`,
              {
                ok: false,
                reason: "VALIDATION",
                nodeId: node.id,
                message: validation.message,
              }
            );
          }
        }

        if (nodeType === "review_and_submit") {
          const validation = validateReviewAndSubmitConfig(
            node?.data?.config || {},
            payload.nodes,
            payload.edges,
            node.id
          );
          if (!validation.isValid) {
            return failWithErrorToast(
              `${getValidationNodeLabel(node)}: ${validation.message}`,
              {
                ok: false,
                reason: "VALIDATION",
                nodeId: node.id,
                message: validation.message,
              }
            );
          }
        }

        if (nodeType === "prepare_commerce_service") {
          const validation = validatePrepareCommerceServiceConfig(
            node?.data?.config || {},
            node?.data || {},
          );
          if (!validation.isValid) {
            return failWithErrorToast(`${getValidationNodeLabel(node)}: ${validation.message}`, {
              ok: false, reason: "VALIDATION", nodeId: node.id, message: validation.message,
            });
          }
        }

        if (nodeType === "whatsapp_flow") {
          const validation = validateWhatsAppFlowConfig(node?.data?.config || {});
          if (!validation.isValid) {
            return failWithErrorToast(
              `${getValidationNodeLabel(node)}: ${validation.message}`,
              {
                ok: false,
                reason: "VALIDATION",
                nodeId: node.id,
                message: validation.message,
              }
            );
          }
        }
        }
      }

      const nodesDto =
        payload.nodes?.map((node, idx) => {
          const normalizedNodeType =
            node.type === "choice"
              ? "button_choice"
              : node.type === "condition"
              ? "branch"
              : node.type;

          // Map canvas graph into AutoReplyNodeDto expected by backend
          let config = {
            ...(node.data?.config || {}),
          };

          if (!requiresExecutableValidation) {
            config = {
              ...(node.data?.config || {}),
            };
          } else if (normalizedNodeType === "button_choice") {
            const options = Array.isArray(config.options) ? config.options : [];
            const savedHeaderMedia = config.headerMedia;
            config = {
              promptText:
                String(config.promptText || config.message || "").trim() ||
                "Please choose an option:",
              options: options.map((opt, index) => ({
                label: String(opt?.label || "").trim(),
                value:
                  typeof opt?.value === "string" && opt.value.trim()
                    ? opt.value.trim()
                    : String(index + 1),
                description: String(opt?.description || "").trim(),
                terms: normalizeChoiceTerms(opt?.terms),
              })),
            };
            // Preserve headerMedia when configured
            if (
              savedHeaderMedia &&
              typeof savedHeaderMedia === "object" &&
              savedHeaderMedia.kind &&
              (savedHeaderMedia.mediaId || savedHeaderMedia.mediaUrl)
            ) {
              config.headerMedia = {
                kind: savedHeaderMedia.kind,
                ...(savedHeaderMedia.mediaId ? { mediaId: savedHeaderMedia.mediaId } : {}),
                ...(savedHeaderMedia.mediaUrl ? { mediaUrl: savedHeaderMedia.mediaUrl } : {}),
                ...(savedHeaderMedia.caption ? { caption: savedHeaderMedia.caption } : {}),
              };
            }
          } else if (normalizedNodeType === "url_cta") {
            config = normalizeUrlCtaConfig(config);
          } else if (normalizedNodeType === "branch" && Array.isArray(config.rules)) {
            config = {
              fieldKey: String(config.fieldKey || "").trim().toLowerCase(),
              comparisonType: String(config.comparisonType || "").trim().toLowerCase(),
              rules: config.rules.map(rule => ({
                pathId: String(rule.pathId || "").trim(),
                label: String(rule.label || "").trim(),
                operator: String(rule.operator || "").trim().toLowerCase(),
                comparisonValue: ["is_empty", "is_not_empty"].includes(rule.operator)
                  ? null
                  : String(rule.comparisonValue || "").trim(),
              })),
              defaultPathId: "default",
            };
          } else if (normalizedNodeType === "browse_catalogue") {
            config = normalizeBrowseCatalogueConfig(config);
          } else if (normalizedNodeType === "search_catalogue") {
            const searchMode = String(config.searchMode || "filtered")
              .trim()
              .toLowerCase();
            const categorySourceNodeId = String(
              config.categorySourceNodeId || ""
            ).trim();
            config = {
              searchMode,
              queryFieldKey:
                searchMode === "all"
                  ? ""
                  : String(config.queryFieldKey || "").trim(),
              catalogId: String(config.catalogId || "").trim() || null,
              maximumResults: Number(config.maximumResults),
              ...(searchMode !== "all" && categorySourceNodeId
                ? { categorySourceNodeId }
                : {}),
            };
          } else if (normalizedNodeType === "present_catalogue_results") {
            const commerce = normalizePresentCatalogueCommerceConfig(
              config.commerce
            );
            config = {
              searchNodeId: String(config.searchNodeId || "").trim(),
              promptText:
                String(config.promptText ?? "").trim() ||
                PRESENT_CATALOGUE_RESULTS_DEFAULT_PROMPT,
              sectionMode: String(config.sectionMode || "flat")
                .trim()
                .toLowerCase(),
              ...(commerce ? { commerce } : {}),
            };
          } else if (normalizedNodeType === "review_and_submit") {
            config = {
              promptText:
                String(config.promptText ?? "").trim() ||
                REVIEW_AND_SUBMIT_DEFAULT_PROMPT,
              detailBindings: (Array.isArray(config.detailBindings)
                ? config.detailBindings
                : []
              ).map((binding, index) => ({
                sourceInputCaptureNodeId: String(
                  binding?.sourceInputCaptureNodeId || ""
                ).trim(),
                fieldKey: String(binding?.fieldKey || "")
                  .trim()
                  .toLowerCase(),
                labelOverride:
                  String(binding?.labelOverride || "").trim() || null,
                sortOrder: index,
              })),
              ...(config?.servicePricing?.source === "DecisionTable"
                ? { servicePricing: {
                      source: "DecisionTable",
                      decisionTableNodeId: String(
                        config.servicePricing.decisionTableNodeId || ""
                      ).trim(),
                      fieldKey: String(config.servicePricing.fieldKey || "")
                        .trim()
                        .toLowerCase(),
                    } }
                : {}),
            };
          } else if (normalizedNodeType === "prepare_commerce_service") {
            config = normalizePrepareCommerceServiceConfig(config);
          }

          const followUp = normalizeFollowUpForSave(node, payload.edges, payload.nodes);
          return {
            // Send null for non-Guid values so backend can generate new ids
            id: toGuidOrNull(node.id),
            nodeType: normalizedNodeType,
            label: node.data?.label || "",
            nodeName: node.data?.label || node.id,
            configJson: JSON.stringify(config),
            positionX: node.position?.x ?? 0,
            positionY: node.position?.y ?? 0,
            order: idx,
            followUp,
          };
        }) || [];
      const edgesDto =
        payload.edges?.map(e => ({
          id: e.id || null,
          sourceNodeId: e.sourceNodeId,
          targetNodeId: e.targetNodeId,
          sourceHandle: e.sourceHandle ?? null,
          targetHandle: e.targetHandle ?? null,
        })) || [];

      // 🔧 normalize priority (fallback to 0 if invalid)
      const dto = {
        id: toGuidOrNull(payload.id) || null,
        phoneNumberId: phoneNumberId || null,
        name: flowName,
        triggerKeyword,
        isActive: savingActive,
        isDraft: savingDraft,
        isDefaultEntryFlow,
        // 🔥 advanced matching settings (optional)
        matchMode: normalizeMatchMode(matchMode),
        priority: safePriority,
        nodes: nodesDto,
        edges: edgesDto,
      };

      let savedData = persistedDraftData;
      if (!activating) {
        const saveEndpoint = savingDraft
          ? "autoreplyflows/draft"
          : "autoreplyflows";
        const saveResponse = await axiosClient.post(saveEndpoint, dto, {
          __suppressQuotaRedirect: true,
          __silentGenericToast: true,
        });
        savedData = saveResponse?.data;
      }

      let data = savedData;
      if (activating) {
        const persistedId = savedData?.id || dto.id || activeFlowId;
        if (!persistedId) {
          return failWithErrorToast("Save the flow before activating it.", {
            ok: false,
            reason: "VALIDATION",
          });
        }
        await axiosClient.patch(`autoreplyflows/${persistedId}/status`, {
          isActive: true,
        });
        const activatedResponse = await axiosClient.get(
          `autoreplyflows/${persistedId}`
        );
        data = activatedResponse.data;
      }

      if (data?.id) {
        setCurrentFlowId(data.id);
        await loadFlowById(data.id, {
          skipDirtyCheck: true,
          ignoreCanvasDraft: true,
        }); // reload to ensure we have the latest server copy/id
      }
      setIsCanvasDirty(false);
      setSidebarRefreshKey(k => k + 1);
      stopSaving();
      toast.success(
        activating
          ? "Flow activated successfully."
        : savingDraft
          ? "Draft saved."
          : "Flow saved successfully."
      );
      return { ok: true, data };
    } catch (err) {
      setRecoverySummaryStatus("unknown");
      const status = err?.response?.status;
      const apiResult = err?.response?.data;
      const errorCode = apiResult?.payload?.code || apiResult?.data?.code;
      const apiMessage =
        apiResult?.message || apiResult?.Message || apiResult?.errorMessage;
      const duplicateKeywords =
        apiResult?.payload?.duplicatedKeywords || apiResult?.data?.duplicatedKeywords || [];
      const conflictingFlows =
        apiResult?.payload?.conflictingFlows || apiResult?.data?.conflictingFlows || [];
      const validationNodeId =
        apiResult?.payload?.nodeId || apiResult?.data?.nodeId || null;

      if (activating && (status === 400 || status === 409)) {
        if (persistedDraftData || isDraft) {
          setIsDraft(true);
          setIsActive(false);
        }
        return failWithErrorToast(
          `Cannot activate flow${apiMessage ? `: ${apiMessage}` : "."}`,
          {
            ok: false,
            reason: "ACTIVATION_VALIDATION",
            nodeId: validationNodeId,
            message: apiMessage || "The flow is not ready to activate.",
          }
        );
      }

      if (status === 409) {
        if (errorCode === "AUTOREPLY_EDIT_LOCKED_ACTIVE_SESSIONS") {
          const activeSessions =
            apiResult?.payload?.activeSessions || apiResult?.data?.activeSessions || 0;

          return failWithErrorToast(
            activeSessions > 0
              ? `This flow is temporarily locked because ${activeSessions} customer session(s) are currently active.`
              : "This flow is temporarily locked while active customer sessions are in progress.",
            { ok: false, reason: "EDIT_LOCKED" },
            { toastId: "autoreply-edit-locked-active-sessions", autoClose: 5000 }
          );
        }

        setDuplicateKeywordModal({
          open: true,
          duplicatedKeywords: Array.isArray(duplicateKeywords) ? duplicateKeywords : [],
          conflictingFlows: Array.isArray(conflictingFlows) ? conflictingFlows : [],
        });
        stopSaving();
        return { ok: false, reason: "DUPLICATE_KEYWORDS" };
      }

      if (status === 429) {
        // 429 quota UX is handled globally (toast + upgrade modal). Keep user on this page.
        stopSaving();
        return { ok: false, reason: "QUOTA" };
      }


      if (status === 400 && apiMessage) {
        return failWithErrorToast(apiMessage, { ok: false, reason: "VALIDATION" });
      }
      console.error("Save error:", err);
      return failWithErrorToast("❌ Failed to save flow", { ok: false, reason: "ERROR" });
    }
  };

  // 🛡️ Confirmation handlers
  const handleConfirmSave = async () => {
    const result = isActive
      ? await canvasRef.current?.saveActiveChanges?.()
      : await canvasRef.current?.saveDraftFlow?.();
    if (result?.ok === false) return;
    
    const idToLoad = pendingFlowId;
    setPendingFlowId(null);
    setShowConfirmModal(false);
    if (idToLoad) {
      loadFlowById(idToLoad, { skipDirtyCheck: true });
    }
  };

  const handleConfirmDiscard = () => {
    const idToLoad = pendingFlowId;
    setPendingFlowId(null);
    setShowConfirmModal(false);
    if (idToLoad) {
      loadFlowById(idToLoad, { skipDirtyCheck: true });
    }
  };

  const handleConfirmCancel = () => {
    setPendingFlowId(null);
    pendingActivationErrorRef.current = null;
    setShowConfirmModal(false);
  };

  const resetNewFlowDraft = () => {
    setNewFlowDraft({
      flowName: "",
      triggerKeywords: "",
      phoneNumberId: "",
      isDefaultEntryFlow: false,
    });
  };

  const openCreateNewFlowModal = () => {
    resetNewFlowDraft();
    setShowEditDetails(false);
    setShowAdvancedAutomation(false);
    setShowRecoveryModal(false);
    setShowNewFlowModal(true);
  };

  const handleNewFlowClick = () => {
    setShowEditDetails(false);
    setShowAdvancedAutomation(false);
    setShowRecoveryModal(false);
    if (isCanvasDirty || canvasRef.current?.hasUnsavedChanges?.()) {
      setShowNewFlowConfirm(true);
      return;
    }
    openCreateNewFlowModal();
  };

  const handleSaveAndCreateNew = async () => {
    const result = isActive
      ? await canvasRef.current?.saveActiveChanges?.()
      : await canvasRef.current?.saveDraftFlow?.();
    if (result?.ok === false) return;
    setShowNewFlowConfirm(false);
    openCreateNewFlowModal();
  };

  const handleDiscardAndCreateNew = () => {
    setShowNewFlowConfirm(false);
    openCreateNewFlowModal();
  };

  const handleCreateNewFlow = () => {
    const nextName = newFlowDraft.flowName.trim();
    const nextTriggers = newFlowDraft.triggerKeywords.trim();

    if (!nextName) {
      toast.error("Flow name is required.");
      return;
    }

    canvasRef.current?.clearFlow?.();
    setCurrentFlowId(null);
    setFlowName(nextName);
    setTriggerKeywords(nextTriggers);
    setPhoneNumberId(newFlowDraft.phoneNumberId);
    setSavedPhoneNumberId(null);
    setLoadedLegacyFlow(false);
    setMatchMode("EXACT");
    setPriority(0);
    setIsActive(false);
    setIsDraft(true);
    setIsDefaultEntryFlow(newFlowDraft.isDefaultEntryFlow);
    setRecoverySummaryStatus("unknown");
    setIsCanvasDirty(true);
    setShowNewFlowModal(false);
  };

  const closeDuplicateKeywordModal = () => {
    setDuplicateKeywordModal({
      open: false,
      duplicatedKeywords: [],
      conflictingFlows: [],
    });
  };

  const handleFlowDeleted = (_flowId, action) => {
    if (action === "HARD_DELETED") {
      canvasRef.current?.clearFlow?.();
      setMatchMode("EXACT");
      setPriority(0);
      setIsActive(false);
      setIsDraft(true);
      setIsDefaultEntryFlow(false);
      setPhoneNumberId("");
      setSavedPhoneNumberId(null);
      setLoadedLegacyFlow(false);
      setCurrentFlowId(null);
      setRecoverySummaryStatus("unknown");
      setIsCanvasDirty(false);
    }
    setSidebarRefreshKey(k => k + 1);
  };

  const surfaceActivationError = useCallback(errorContext => {
    if (!errorContext) return;
    const message =
      errorContext.message || "The flow is not ready to activate.";
    if (METADATA_ACTIVATION_ERROR_CODES.has(errorContext.code)) {
      setShowEditDetails(true);
    }
    toast.error(`Cannot activate flow: ${message}`);
    if (errorContext.nodeId) {
      window.setTimeout(() => {
        canvasRef.current?.showValidationError?.(
          errorContext.nodeId,
          message
        );
      }, 0);
    }
  }, []);

  const loadFlowById = useCallback(async (id, options = {}) => {
    if (!id) return;

    const skipDirtyCheck = options?.skipDirtyCheck === true;
    if (!skipDirtyCheck && canvasRef.current?.hasUnsavedChanges?.()) {
      setPendingFlowId(id);
      setShowConfirmModal(true);
      return;
    }

    try {
      setRecoverySummaryStatus("loading");
      const { data } = await axiosClient.get(`autoreplyflows/${id}`);
      setCurrentFlowId(data?.id || id);

      // 🔥 apply advanced settings from backend (with smart defaults)
      const incomingMode = data?.matchMode;
      const safeMode = normalizeMatchMode(incomingMode);

      const incomingPriority = data?.priority;
      const safeIncomingPriority =
        typeof incomingPriority === "number" && !Number.isNaN(incomingPriority)
          ? incomingPriority
          : 0;

      setMatchMode(safeMode);
      setPriority(safeIncomingPriority);
      setIsActive(data?.isActive === true);
      setIsDraft(data?.isDraft === true);
      setIsDefaultEntryFlow(data?.isDefaultEntryFlow === true);
      setPhoneNumberId(data?.phoneNumberId || "");
      setSavedPhoneNumberId(data?.phoneNumberId || "");
      setLoadedLegacyFlow(!data?.phoneNumberId);
      setIsCanvasDirty(false);

      // keep existing behaviour for canvas
      canvasRef.current?.loadFlow(data, {
        ignoreDraft: options?.ignoreCanvasDraft === true,
      });

      const pendingActivationError = pendingActivationErrorRef.current;
      if (
        pendingActivationError &&
        String(pendingActivationError.flowId) === String(data?.id || id)
      ) {
        pendingActivationErrorRef.current = null;
        surfaceActivationError(pendingActivationError);
      }

      try {
        const recoveryResponse = await axiosClient.get(
          `autoreplyflows/${data?.id || id}/recovery-settings`
        );
        setRecoverySummaryStatus(mapRecoverySummaryStatus(recoveryResponse?.data));
      } catch (recoveryError) {
        setRecoverySummaryStatus("unknown");
        console.error("Error loading recovery settings summary:", recoveryError);
      }
    } catch (err) {
      toast.error("❌ Error loading flow");
      console.error(err);
    }
  }, [normalizeMatchMode, surfaceActivationError]);

  const handleActivationValidationFailure = useCallback(
    errorContext => {
      if (!errorContext?.flowId) return;
      pendingActivationErrorRef.current = errorContext;
      setShowFlowsModal(false);
      loadFlowById(errorContext.flowId);
    },
    [loadFlowById]
  );

  useEffect(() => {
    let cancelled = false;
    axiosClient
      .get("/whatsappsettings/META_CLOUD/numbers")
      .then(({ data }) => {
        if (!cancelled) setPhoneNumbers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setPhoneNumbers([]);
      });
    return () => { cancelled = true; };
  }, [business?.id]);

  useEffect(() => {
    if (flowId) {
      loadFlowById(flowId, { skipDirtyCheck: true });
    }
  }, [flowId, loadFlowById]);

  const duplicateKeywordNames = duplicateKeywordModal.duplicatedKeywords
    .filter(Boolean)
    .join(", ");

  const duplicateFlowNames = Array.from(
    new Set(
      duplicateKeywordModal.conflictingFlows
        .map(flow => String(flow?.flowName || "").trim())
        .filter(Boolean)
    )
  );

  const duplicateKeywordMessage = (
    <div className="space-y-3">
      <p>
        {duplicateKeywordNames
          ? `The trigger keyword${duplicateKeywordModal.duplicatedKeywords.length === 1 ? "" : "s"} "${duplicateKeywordNames}" ${duplicateKeywordModal.duplicatedKeywords.length === 1 ? "is" : "are"} already used in another active flow.`
          : "This trigger keyword is already used in another active flow."}
      </p>
      <p>Please choose a different trigger keyword and try saving again.</p>
      {duplicateFlowNames.length > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-700">
          <p className="font-semibold text-slate-900">
            {duplicateFlowNames.length === 1 ? "Already used in flow:" : "Already used in flows:"}
          </p>
          <p>{duplicateFlowNames.join(", ")}</p>
        </div>
      ) : null}
    </div>
  );
  const canSaveDraft =
    !isActive && hasUserNodes && isCanvasDirty && !isSavingFlow;
  const canSaveAndActivate =
    !isActive && hasUserNodes && !isSavingFlow;
  const canSaveActiveChanges =
    isActive && isCanvasDirty && !isSavingFlow;

  return (
    <div className="relative flex h-[calc(100vh-80px)] min-h-0 flex-col overflow-hidden bg-slate-50 shadow-inner">
      {/* Main Area */}
      <div className="flex min-h-0 flex-1 flex-col bg-[#f5f6f7]">
        <div className="px-6 pt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3 font-['Plus_Jakarta_Sans']">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 shadow-sm shadow-emerald-200">
                <Workflow size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900">
                  Chatbot Builder
                </h1>
                <p className="text-xs text-slate-500">
                  Design the flow canvas and configure routing details.
                </p>
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowEditDetails(true)}
                className="inline-flex max-w-[300px] items-center overflow-hidden rounded-full border border-emerald-100 bg-white text-left shadow-sm transition-colors hover:border-emerald-200"
                title="Edit flow details"
              >
                <span className="bg-emerald-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Flow
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5 bg-white px-3 py-1.5 text-sm font-medium text-slate-800">
                  <span className="truncate">{visibleFlowName}</span>
                  <Pencil size={13} className="shrink-0 text-slate-400" />
                </span>
              </button>

              <span className="inline-flex max-w-[340px] items-center overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm">
                <span className="bg-sky-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
                  Sender
                </span>
                <span className="min-w-0 bg-white px-3 py-1.5 text-sm font-medium text-slate-700">
                  <span className="block truncate">{senderLabel}</span>
                </span>
              </span>

              {isDefaultEntryFlow ? (
                <span className="inline-flex rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700">
                  Default
                </span>
              ) : null}

              <button
                type="button"
                onClick={() => setShowEditDetails(true)}
                aria-label="Edit flow details"
                title="Edit flow details"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-emerald-200 hover:bg-slate-50 hover:text-emerald-700"
              >
                <Pencil size={17} />
              </button>

              <button
                type="button"
                onClick={handleNewFlowClick}
                aria-label="Create new flow"
                title="Create new flow"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100"
              >
                <Plus size={18} />
              </button>

              <button
                type="button"
                onClick={handleOpenFlowsModal}
                aria-label="Manage flows"
                title="Manage flows"
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-emerald-200 hover:bg-slate-50 hover:text-emerald-700"
              >
                <LayoutList size={17} />
              </button>

              {!isActive ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleSaveCanvasFlow("draft")}
                    disabled={!canSaveDraft}
                    aria-busy={
                      isSavingFlow && savingMode === "draft" ? "true" : "false"
                    }
                    className="rounded-md border border-emerald-600 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {isSavingFlow && savingMode === "draft"
                      ? "Saving Draft..."
                      : "Save as Draft"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveCanvasFlow("activate")}
                    disabled={!canSaveAndActivate}
                    aria-busy={
                      isSavingFlow && savingMode === "activate"
                        ? "true"
                        : "false"
                    }
                    className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
                  >
                    {isSavingFlow && savingMode === "activate"
                      ? "Saving & Activating..."
                      : "Save & Activate"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => handleSaveCanvasFlow("save_active")}
                  disabled={!canSaveActiveChanges}
                  aria-busy={
                    isSavingFlow && savingMode === "save_active"
                      ? "true"
                      : "false"
                  }
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white shadow transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none"
                >
                  {isSavingFlow && savingMode === "save_active"
                    ? "Saving Changes..."
                    : "Save Changes"}
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          data-testid="auto-reply-builder-workspace"
          className="grid min-h-0 flex-1 grid-cols-[224px_minmax(0,1fr)] gap-3 overflow-hidden bg-slate-50/50 p-3"
        >
          <div
            data-testid="auto-reply-palette-column"
            className="flex min-h-0 justify-center overflow-hidden border-r border-slate-200 bg-slate-50/40"
          >
            <AutoReplySidebar
              key={sidebarRefreshKey}
              onAddNode={handleAddNodeFromSidebar}
            />
          </div>

          {/* Canvas + Settings */}
          <div
            data-testid="auto-reply-canvas-column"
            className="relative min-h-0 min-w-0 overflow-hidden"
          >
          <AutoReplyCanvas
            ref={canvasRef}
            flowName={flowName}
            triggerKeywords={triggerKeywords}
            onFlowNameChange={setFlowName}
            onTriggerKeywordsChange={setTriggerKeywords}
            onDirtyChange={setIsCanvasDirty}
            onContentStateChange={({ hasUserNodes: nextHasUserNodes }) =>
              setHasUserNodes(nextHasUserNodes)
            }
            onSave={handleSaveFlow}
            matchMode={matchMode}
            priority={priority}
            isActive={isActive}
            onMatchModeChange={setMatchMode}
            onPriorityChange={handlePriorityChange}
            onIsActiveChange={setIsActive}
            sourcePhoneNumberId={phoneNumberId}
          />

          {isSavingFlow && (
            <div
              className="absolute inset-0 z-[80] flex items-center justify-center bg-white/55 backdrop-blur-[2px]"
              aria-live="polite"
              aria-label="Saving flow"
            >
              <div className="flex min-w-[260px] flex-col items-center rounded-lg border border-slate-200 bg-white/95 px-6 py-5 text-center shadow-lg">
                <Loader2
                  size={28}
                  className="mb-3 animate-spin text-emerald-600"
                  aria-hidden="true"
                  data-testid="saving-flow-spinner"
                />
                <div className="text-sm font-semibold text-slate-900">Saving flow...</div>
                <div className="mt-1 text-xs text-slate-500">
                  Please wait while we save your changes.
                </div>
              </div>
            </div>
          )}

          {/* 🔧 Flow-level advanced settings (optional) */}
          </div>
        </div>
      </div>

      {showEditDetails && (
        <div className="fixed inset-0 z-[1000] flex justify-end bg-slate-950/40 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Edit flow details">
          <button
            type="button"
            className="absolute inset-0 cursor-default"
            aria-label="Close edit details"
            onClick={() => {
              setShowEditDetails(false);
              setShowAdvancedAutomation(false);
            }}
          />
          <div className="relative flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Edit Details</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Configure flow metadata, sender routing, matching, and recovery options.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowEditDetails(false);
                  setShowAdvancedAutomation(false);
                }}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close edit details"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700">
                      Flow name
                    </label>
                    <input
                      type="text"
                      value={flowName}
                      onChange={e => updateFlowName(e.target.value)}
                      placeholder="Flow name"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700">
                      Trigger keyword(s)
                    </label>
                    <input
                      type="text"
                      value={triggerKeywords}
                      onChange={e => updateTriggerKeywords(e.target.value)}
                      placeholder="Triggers (comma separated)"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-700">
                    WhatsApp sender
                  </label>
                  <select
                    value={phoneNumberId}
                    onChange={e => updatePhoneNumberId(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    aria-label="WhatsApp number for this flow"
                  >
                    <option value="">All Number</option>
                    {phoneNumberId && !selectedPhoneNumber && (
                      <option value={phoneNumberId}>
                        {phoneNumberId} (unavailable)
                      </option>
                    )}
                    {phoneNumbers.map(number => (
                      <option key={number.phoneNumberId} value={number.phoneNumberId}>
                        {formatSenderDisplayLabel(number)}
                      </option>
                    ))}
                  </select>
                  {!phoneNumberId && (
                    <p className="mt-1 text-xs text-amber-700">
                      All Number routing applies to all WhatsApp numbers until you assign a specific sender.
                    </p>
                  )}
                  {isDefaultEntryFlow && !phoneNumberId && (
                    <p className="mt-1 text-xs text-rose-600">
                      {DEFAULT_ENTRY_SENDER_REQUIRED_MESSAGE}
                    </p>
                  )}
                  {senderChangedAfterLoad && (
                    <p className="mt-1 text-xs text-slate-500">
                      Changing sender affects where this flow can run after saving.
                    </p>
                  )}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700">
                      Match mode
                    </label>
                    <select
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      value={matchMode || "EXACT"}
                      onChange={e => updateMatchMode(e.target.value)}
                    >
                      <option value="EXACT">EXACT (default)</option>
                      <option value="PHRASE">PHRASE (word-boundary)</option>
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                      EXACT matches full message. PHRASE matches boundary-safe phrases.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-700">
                      Priority
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                      value={priority}
                      onChange={e => updatePriority(e.target.value)}
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Flow status</p>
                      <p className="text-xs text-slate-500">
                        Draft and inactive flows are ignored by runtime matching.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={
                        isActive
                          ? handleDeactivateFlow
                          : handleActivateCanvasFlow
                      }
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                        isDraft
                          ? "border-amber-200 bg-amber-50 text-amber-700"
                          : isActive
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-zinc-300 bg-zinc-100 text-zinc-700"
                      }`}
                    >
                      {isDraft ? "Draft" : isActive ? "Active" : "Inactive"}
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Advanced automation
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Default entry and invalid reply recovery settings.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAdvancedAutomation(prev => !prev)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-emerald-200 hover:text-emerald-700"
                    >
                      <Settings size={15} />
                      Advanced settings
                    </button>
                  </div>

                  {showAdvancedAutomation && (
                    <div className="mt-4 space-y-4 rounded-xl border border-slate-200 bg-white p-4">
                      <div>
                        <h3 className="text-sm font-bold text-slate-900">
                          Advanced Automation Settings
                        </h3>
                        <p className="mt-1 text-xs text-slate-500">
                          Configure fallback routing for unmatched or invalid customer messages.
                        </p>
                      </div>

                      <div className="rounded-xl border border-slate-200 p-4">
                        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={isDefaultEntryFlow}
                            disabled={!phoneNumberId}
                            onChange={e => updateIsDefaultEntryFlow(e.target.checked)}
                            className="h-4 w-4 accent-emerald-600"
                          />
                          Start this flow for any customer message
                        </label>
                        <p className="mt-1 text-xs text-slate-500">
                          Starts only for unmatched text on this WhatsApp number. Existing sessions and keyword triggers win.
                        </p>
                        {!phoneNumberId && (
                          <p className="mt-1 text-xs text-amber-600">
                            {DEFAULT_ENTRY_SENDER_REQUIRED_MESSAGE}
                          </p>
                        )}
                      </div>

                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Invalid Reply Recovery</p>
                            <p className="mt-1 text-xs leading-relaxed text-slate-500">
                              Shown when a customer is already inside this flow and sends a reply the bot cannot understand.
                            </p>
                          </div>
                          <span
                            className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                              recoverySummaryStatus === "enabled"
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : recoverySummaryStatus === "disabled"
                                ? "border-slate-200 bg-slate-50 text-slate-600"
                                : recoverySummaryStatus === "loading"
                                ? "border-slate-200 bg-white text-slate-500"
                                : "border-amber-200 bg-amber-50 text-amber-700"
                            }`}
                          >
                            {recoverySummaryStatus === "enabled"
                              ? "Enabled"
                              : recoverySummaryStatus === "disabled"
                              ? "Disabled"
                              : recoverySummaryStatus === "loading"
                              ? "Loading…"
                              : "Not configured"}
                          </span>
                        </div>
                        <button
                          type="button"
                          disabled={!activeFlowId}
                          onClick={() => {
                            if (!activeFlowId) return;
                            setShowEditDetails(false);
                            setShowAdvancedAutomation(false);
                            setShowRecoveryModal(true);
                          }}
                          className="mt-3 w-full rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
                        >
                          Configure Recovery Options
                        </button>
                        {!activeFlowId && (
                          <p className="mt-1 text-xs text-amber-600">
                            Save this flow first to configure recovery options.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button
                type="button"
                className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                onClick={() => {
                  updateMatchMode("EXACT");
                  updatePriority(0);
                }}
              >
                Reset matching defaults
              </button>
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                onClick={() => {
                  setShowEditDetails(false);
                  setShowAdvancedAutomation(false);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewFlowConfirm && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Create new flow confirmation">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-100 px-6 py-5">
              <h2 className="text-lg font-bold text-slate-900">Create new flow?</h2>
              <p className="mt-1 text-sm text-slate-500">
                You have unsaved changes in the current flow. Choose how to continue.
              </p>
            </div>
            <div className="flex flex-col gap-3 bg-slate-50 px-6 py-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowNewFlowConfirm(false)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDiscardAndCreateNew}
                className="rounded-lg border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50"
              >
                Discard and create new
              </button>
              <button
                type="button"
                onClick={handleSaveAndCreateNew}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Save and create new
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewFlowModal && (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Create New Flow">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Create New Flow</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Set the flow metadata first. You can design the canvas after creating it locally.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowNewFlowModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Close create new flow"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Flow name</label>
                <input
                  type="text"
                  value={newFlowDraft.flowName}
                  onChange={e => setNewFlowDraft(prev => ({ ...prev, flowName: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Flow name"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Trigger keyword(s)</label>
                <input
                  type="text"
                  value={newFlowDraft.triggerKeywords}
                  onChange={e => setNewFlowDraft(prev => ({ ...prev, triggerKeywords: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Triggers (comma separated)"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">WhatsApp sender</label>
                <select
                  value={newFlowDraft.phoneNumberId}
                  onChange={e => setNewFlowDraft(prev => ({ ...prev, phoneNumberId: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  <option value="">Select WhatsApp number</option>
                  {phoneNumbers.map(number => (
                    <option key={number.phoneNumberId} value={number.phoneNumberId}>
                      {formatSenderDisplayLabel(number)}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 p-3 text-sm font-semibold text-slate-700">
                <input
                  type="checkbox"
                  checked={newFlowDraft.isDefaultEntryFlow}
                  onChange={e => setNewFlowDraft(prev => ({ ...prev, isDefaultEntryFlow: e.target.checked }))}
                  className="h-4 w-4 accent-emerald-600"
                />
                Start this flow for any customer message
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowNewFlowModal(false)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateNewFlow}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flows Modal */}
      <AutoReplyFlowsModal
        open={showFlowsModal}
        onClose={() => setShowFlowsModal(false)}
        onFlowDeleted={handleFlowDeleted}
        onActivationValidationFailed={handleActivationValidationFailure}
        onFlowSelected={flowIdOrFlow => {
          // You were passing `flow.id` earlier; now we support both id or object.
          const id =
            typeof flowIdOrFlow === "string"
              ? flowIdOrFlow
              : flowIdOrFlow?.id ?? null;
          if (id) {
            loadFlowById(id);
          }
        }}
      />

      {/* 🛡️ Unsaved Changes Confirmation */}
      <AutoReplyRecoveryOptionsModal
        open={showRecoveryModal}
        flowId={activeFlowId}
        onClose={() => setShowRecoveryModal(false)}
        onLoaded={settings =>
          setRecoverySummaryStatus(mapRecoverySummaryStatus(settings))
        }
        onSaved={settings =>
          setRecoverySummaryStatus(settings?.isEnabled ? "enabled" : "disabled")
        }
      />

      <AutoReplyConfirmModal
        open={showConfirmModal}
        onClose={handleConfirmCancel}
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
      />
      <AutoReplyAlertModal
        open={duplicateKeywordModal.open}
        onClose={closeDuplicateKeywordModal}
        onConfirm={closeDuplicateKeywordModal}
        title="Trigger keyword already in use"
        message={duplicateKeywordMessage}
        confirmLabel="OK"
        cancelLabel={null}
        intent="info"
        dismissible={false}
      />
    </div>
  );
}
